// Reusable mini day switcher component - extracted from AssignedRoomsPanel

import React from "react";
import type { OfferDay } from "../../../../types";

interface MiniDaySwitcherProps {
  /** All accommodation days (including checkout day) */
  days: OfferDay[];
  /** Index of the currently selected day */
  currentDayIndex: number;
  /** Callback when user selects a different day */
  onDayChange: (dayIndex: number) => void;
}

/**
 * Mini day navigation component for switching between accommodation nights.
 * Renders one button per night (excludes the last checkout day).
 */
export const MiniDaySwitcher: React.FC<MiniDaySwitcherProps> = ({
  days,
  currentDayIndex,
  onDayChange,
}) => {
  // Exclude the last checkout day - only show night days
  const nightDays = days.slice(0, -1);

  if (nightDays.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      {nightDays.map((dayData, index) => {
        const date = new Date(dayData.date);
        const dayMonth = date.toLocaleDateString("pl-PL", {
          day: "numeric",
          month: "numeric",
        });
        const dayName = date.toLocaleDateString("pl-PL", {
          weekday: "short",
        });

        const isActive = currentDayIndex === index;

        return (
          <button
            key={dayData.id}
            onClick={() => onDayChange(index)}
            aria-label={`Noc ${index + 1} - ${dayMonth}`}
            aria-pressed={isActive}
            aria-current={isActive ? "true" : undefined}
            className={`
              px-2 py-1 rounded text-xs font-medium transition-colors text-center min-w-[44px]
              ${
                isActive
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }
            `}
          >
            <div className="text-xs font-bold">{index + 1}</div>
            {dayMonth && (
              <>
                <div className="text-xs leading-3">{dayMonth}</div>
                <div className="text-xs leading-3">
                  {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                </div>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};
